import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
const useResizeObserver = (callback) => {
    const ref = useRef();
    useEffect(() => {
        return () => ref.current && ref.current.disconnect();
    }, []);
    const currentObserver = ref.current;
    if (!currentObserver) {
        ref.current = new ResizeObserver(callback);
    }
    const observe = useCallback((element) => {
        ref.current.observe(element);
    }, []);
    const unobserve = useCallback((element) => {
        ref.current.unobserve(element);
    }, []);
    return [observe, unobserve];
};
export const Context = React.createContext([
    () => {
        console.error('using the height observer context without a provider'); // eslint-disable-line no-console
        return () => undefined;
    },
    () => {
        console.error('using the height observer context without a provider'); // eslint-disable-line no-console
        return () => undefined;
    },
]);
const HeightObserverContextWrapper = ({ children }) => {
    const watchedElements = useRef(new WeakSet());
    const recordedRects = useRef(new Map());
    const callbacks = useRef(new Map());
    const [observe, unobserve] = useResizeObserver((entries) => {
        for (const entry of entries) {
            const id = entry.target.getAttribute('id');
            if (!id) {
                continue;
            }
            recordedRects.current.set(id, entry.contentRect);
            for (const cb of callbacks.current.get(id) || []) {
                cb(entry.contentRect);
            }
        }
    });
    const value = useMemo(() => {
        const zeroRect = { height: 0, width: 0, x: 0, y: 0, bottom: 0, left: 0, right: 0, top: 0, toJSON() { } };
        const getCallbacks = (id) => callbacks.current.get(id) || [];
        const reportCurrentRect = (elementId) => {
            var _a;
            if (!elementId || !recordedRects.current.has(elementId))
                return;
            (_a = getCallbacks(elementId)) === null || _a === void 0 ? void 0 : _a.forEach((cb) => cb(recordedRects.current.get(elementId)));
        };
        const sharedObserve = (element) => {
            const elementId = element.getAttribute('id');
            if (!elementId) {
                console.error('watched element does not have an id'); // eslint-disable-line no-console
                return () => { };
            }
            if (watchedElements.current.has(element)) {
                console.warn('already watching'); // eslint-disable-line no-console
            }
            else {
                observe(element);
                watchedElements.current.add(element);
            }
            return () => {
                unobserve(element);
                watchedElements.current.delete(element);
                recordedRects.current.set(elementId, zeroRect);
                reportCurrentRect(elementId);
            };
        };
        const sharedListen = (id, callback) => {
            var _a;
            if (recordedRects.current.has(id)) {
                callback(recordedRects.current.get(id));
            }
            if (callbacks.current.has(id)) {
                (_a = callbacks.current.get(id)) === null || _a === void 0 ? void 0 : _a.push(callback);
            }
            else {
                callbacks.current.set(id, [callback]);
            }
            return () => {
                var _a;
                const leftoverCallbacks = (_a = callbacks.current.get(id)) === null || _a === void 0 ? void 0 : _a.filter((cb) => cb !== callback);
                if (leftoverCallbacks === null || leftoverCallbacks === void 0 ? void 0 : leftoverCallbacks.length) {
                    callbacks.current.set(id, leftoverCallbacks);
                }
                else {
                    callbacks.current.delete(id);
                }
            };
        };
        return [sharedObserve, sharedListen];
    }, [observe, unobserve]);
    return _jsx(Context.Provider, { value: value, children: children });
};
export default HeightObserverContextWrapper;
